import { useEffect, useState, useCallback } from "react";
import { usePostHog } from "posthog-js/react";
import type { Survey } from "posthog-js";
import {
  getStorageWithExpiry,
  setStorageWithExpiry,
} from "../utils/localStorage";

/**
 * Default number of hours until the survey can be shown again after submission/dismissal.
 */
const DEFAULT_SURVEY_EXPIRY_HOURS = 24;

/**
 * Survey response state for all questions
 */
export interface SurveyResponses {
  [questionId: string]: string | number | null;
}

/**
 * Return type for the usePostHogSurvey hook
 */
export interface UsePostHogSurveyReturn {
  /** The active survey, if one matches */
  survey: Survey | null;
  /** Current responses for each question */
  responses: SurveyResponses;
  /** Update a response for a specific question */
  setResponse: (questionId: string, value: string | number | null) => void;
  /** Submit the survey responses to PostHog */
  submitSurvey: () => void;
  /** Dismiss the survey without submitting */
  dismissSurvey: () => void;
  /** Whether the survey is visible */
  isVisible: boolean;
}

/**
 * Options for the usePostHogSurvey hook
 */
export interface UsePostHogSurveyOptions {
  /** The PostHog survey ID to look for */
  surveyId: string;
  /**
   * A value that triggers re-checking survey conditions when it changes.
   * Useful for surveys with selector conditions that depend on DOM state.
   * @example Pass `messages.length` to re-check when new messages are added
   */
  recheckTrigger?: number | string;
  /**
   * Number of hours until the survey can be shown again after submission/dismissal.
   * If not provided, defaults to DEFAULT_SURVEY_EXPIRY_HOURS.
   * If null, the survey will never be shown again (never expires).
   */
  expiryHours?: number | null;
  /**
   * An optional session identifier to scope the survey handling.
   * When provided, the survey "handled" state is tied to this specific session,
   * so the survey can reappear in a different session even if not expired.
   * Typically the chat session ID from ChatContext.
   */
  sessionId?: string;
}

/**
 * Generic hook for displaying PostHog API surveys.
 *
 * Uses PostHog's `getActiveMatchingSurveys` to fetch surveys that match
 * the current user/page conditions. Re-checks when `recheckTrigger` changes
 * to detect when selector conditions become true.
 *
 * PostHog handles tracking whether a user has already seen/interacted with
 * the survey (via wait periods, completion tracking, etc.).
 *
 * Based on: https://posthog.com/tutorials/react-surveys
 *
 * @param options - Configuration options including surveyId and optional recheckTrigger
 * @returns Survey data and methods for rendering and submitting
 *
 * @example
 * ```tsx
 * const { survey, isVisible, responses, setResponse, submitSurvey, dismissSurvey } =
 *   usePostHogSurvey({
 *     surveyId: "019b1229-6c1d-0000-266e-bdea6dff50c7",
 *     recheckTrigger: messages.length,
 *   });
 * ```
 */
export const usePostHogSurvey = (
  options: UsePostHogSurveyOptions
): UsePostHogSurveyReturn => {
  const { surveyId, recheckTrigger, expiryHours, sessionId } = options;
  const posthog = usePostHog();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [responses, setResponses] = useState<SurveyResponses>({});
  const [showSurvey, setShowSurvey] = useState(false);

  // Use provided expiry hours or fall back to default from config
  // null means never expire (survey won't be shown again)
  const surveyExpiryHours =
    expiryHours === undefined ? DEFAULT_SURVEY_EXPIRY_HOURS : expiryHours;

  // Get localStorage key for this specific survey, optionally scoped to a session.
  // When a sessionId is provided the survey "handled" state is per-session,
  // so the survey can reappear in a new chat session.
  const storageKey = sessionId
    ? `posthogSurveyHandled_${surveyId}_${sessionId}`
    : `posthogSurveyHandled_${surveyId}`;

  // Initialize hasHandledSurvey from localStorage with expiry check
  // If the stored value exists and hasn't expired, the survey has been handled
  const [hasHandledSurvey, setHasHandledSurvey] = useState(() => {
    try {
      const stored = getStorageWithExpiry(storageKey);
      return stored === "handled";
    } catch {
      return false;
    }
  });

  // Helper to mark survey as handled and persist to localStorage with custom expiry
  const markSurveyAsHandled = useCallback(() => {
    setHasHandledSurvey(true);
    try {
      setStorageWithExpiry(storageKey, "handled", surveyExpiryHours);
    } catch (error) {
      console.error("[usePostHogSurvey] Error saving to localStorage:", error);
    }
  }, [storageKey, surveyExpiryHours]);

  // Re-check localStorage on mount and when surveyId changes to handle expiry
  useEffect(() => {
    try {
      const stored = getStorageWithExpiry(storageKey);
      setHasHandledSurvey(stored === "handled");
    } catch {
      setHasHandledSurvey(false);
    }
  }, [storageKey]);

  // Fetch active matching surveys when recheckTrigger changes
  // This re-checks conditions (like selector presence) after DOM updates
  useEffect(() => {
    if (!posthog || hasHandledSurvey || showSurvey) {
      // Don't re-check if we've already handled the survey or it's currently showing
      return;
    }

    const fetchSurveys = () => {
      try {
        // Check for matching surveys (conditions evaluated against current DOM state)
        // PostHog handles wait periods internally, so we trust its response
        posthog.getActiveMatchingSurveys((surveys) => {
          if (surveys.length > 0) {
            // Find our specific survey by ID
            const matchedSurvey = surveys.find((s) => s.id === surveyId);

            if (matchedSurvey) {
              setSurvey(matchedSurvey);
              setShowSurvey(true);
            }
          }
        }, true); // forceReload = true to ensure fresh evaluation
      } catch (error) {
        console.error("[usePostHogSurvey] Error fetching surveys:", error);
      }
    };

    // Small delay to allow DOM to update before checking selector conditions
    const timeoutId = setTimeout(fetchSurveys, 500);

    return () => clearTimeout(timeoutId);
  }, [posthog, surveyId, recheckTrigger, hasHandledSurvey, showSurvey]);

  // Capture "survey shown" event when survey becomes visible
  useEffect(() => {
    if (posthog && showSurvey && survey) {
      posthog.capture("survey shown", {
        $survey_id: survey.id,
        $survey_name: survey.name,
      });
    }
  }, [posthog, showSurvey, survey]);

  /**
   * Update a response for a specific question
   */
  const setResponse = useCallback(
    (questionId: string, value: string | number | null) => {
      setResponses((prev) => ({
        ...prev,
        [questionId]: value,
      }));
    },
    []
  );

  /**
   * Submit the survey responses to PostHog
   */
  const submitSurvey = useCallback(() => {
    if (!survey || !posthog) {
      return;
    }

    // Build the response payload
    // PostHog expects responses as $survey_response_{questionId}
    const responsePayload: Record<string, unknown> = {
      $survey_id: survey.id,
      $survey_name: survey.name,
      $survey_questions: survey.questions.map((q) => ({
        id: q.id,
        question: q.question,
      })),
    };

    // Add each question's response
    survey.questions.forEach((question) => {
      if (!question.id) return;
      const responseKey = `$survey_response_${question.id}`;
      responsePayload[responseKey] = responses[question.id] ?? null;
    });

    posthog.capture("survey sent", responsePayload);

    // Hide the survey after submission and mark as handled
    setShowSurvey(false);
    setSurvey(null);
    setResponses({});
    markSurveyAsHandled();
  }, [survey, responses, posthog, markSurveyAsHandled]);

  /**
   * Dismiss the survey without submitting
   */
  const dismissSurvey = useCallback(() => {
    if (posthog && survey) {
      posthog.capture("survey dismissed", {
        $survey_id: survey.id,
        $survey_name: survey.name,
      });
    }

    // Hide the survey after dismissal and mark as handled
    setShowSurvey(false);
    setSurvey(null);
    setResponses({});
    markSurveyAsHandled();
  }, [survey, posthog, markSurveyAsHandled]);

  return {
    survey,
    responses,
    setResponse,
    submitSurvey,
    dismissSurvey,
    isVisible: showSurvey,
  };
};
